// PreviewBookPage — public 3D flipbook at /preview/:token (no login).
//
// The client receives this link to review their album before printing:
//   landing (closed 3D book, their real cover) → tap → page-curl reader
//   (page-flip / StPageFlip engine) → per-page sticky notes → confirm.
//
// Architecture notes (IMPORTANT — do not "react-ify" the book):
//   • PageFlip REPARENTS the page DOM nodes it is given. If React owned those
//     nodes it would crash on removeChild during unmount. So the page elements
//     are built imperatively (buildPageElement) inside a useEffect and React
//     only owns the shell, overlays and sheets around the book.
//   • Lazy image windowing: every page always shows its inline LQIP (a tiny
//     blurred data-URI from the manifest — zero network cost). Sharp JPEGs are
//     attached only within ±SHARP_WINDOW of the current page and detached
//     beyond ±KEEP_WINDOW so a 48-page album never holds more than a handful
//     of decoded bitmaps. All swaps are imperative on the 'flip' event.
//   • NEVER mention a physical paper size anywhere on this page (spec).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { PageFlip } from 'page-flip';
import {
  previewLinkApi,
  type PreviewAlbumData,
  type PreviewNote,
  type PreviewPublicData,
} from '@/lib/api';
import { flipIndexForPage, initialAlbumIdx, parseAlbumParam, parsePageParam } from '@/lib/previewAlbumParam';
import { isCoverNotePage, notePageFor, notePlaceLabel } from '@/lib/previewNoteTarget';
import { albumIdxAfterSwap, bookVersionKey, shouldSwapBook, POLL_OUTDATED_MS, POLL_WATCH_MS, POLL_BUILDING_MS, buildingPctFor } from '@/lib/previewBookVersion';

// ─── Tunables ────────────────────────────────────────────────────────────────
const SHARP_WINDOW = 4;   // pages around the current index that get sharp JPEGs
const KEEP_WINDOW = 8;    // beyond this distance sharp bitmaps are released
const MAX_NOTE_CHARS = 600;

// ─── Page model ──────────────────────────────────────────────────────────────
interface PageMeta {
  kind: 'cover-front' | 'inside-cover' | 'page' | 'cover-back';
  /** Interior PDF page number (1-based); 0 for covers. */
  n: number;
  url: string | null;
  lqip: string | null;
}

interface PageEl {
  meta: PageMeta;
  root: HTMLElement;
  img: HTMLImageElement;
  dot: HTMLElement;
}

function buildMetas(album: PreviewAlbumData): PageMeta[] {
  const metas: PageMeta[] = [];
  metas.push({
    kind: 'cover-front', n: 0,
    url: album.cover?.front?.url ?? null,
    lqip: album.cover?.front?.lqip ?? null,
  });
  // PHYSICAL-BOOK PAIRING (2026-07-05): opening a real hardcover shows the
  // BLANK INSIDE of the front cover on the left, with interior page 1 facing
  // it on the right — page 1 was previously paired with page 2. These blank
  // inside-cover sheets are PREVIEW-ONLY (this list is built from the preview
  // manifest; the PDF/print pipeline never sees them). The mirror blank
  // before the back cover keeps every following spread paired (2,3)(4,5)…
  // exactly like the bound book.
  metas.push({ kind: 'inside-cover', n: 0, url: null, lqip: null });
  for (const p of album.pages) {
    metas.push({ kind: 'page', n: p.n, url: p.url, lqip: p.lqip });
  }
  metas.push({ kind: 'inside-cover', n: 0, url: null, lqip: null });
  metas.push({
    kind: 'cover-back', n: 0,
    url: album.cover?.back?.url ?? null,
    lqip: album.cover?.back?.lqip ?? null,
  });
  return metas;
}

/** Vanilla-DOM page element — PageFlip will reparent it, React must not own it. */
function buildPageElement(meta: PageMeta): PageEl {
  const root = document.createElement('div');
  // inside-cover sheets render as plain white paper (soft density so the
  // page-curl feels like paper, matching how the endpaper turns).
  const hard = meta.kind === 'cover-front' || meta.kind === 'cover-back';
  root.className = `pv-page${hard ? ' pv-page--hard' : ''}`;
  if (hard) root.setAttribute('data-density', 'hard');
  if (meta.kind === 'inside-cover') root.style.background = '#ffffff';

  // Blurred inline placeholder — always present, sharp image fades in above it.
  const lqip = document.createElement('div');
  lqip.className = 'pv-lqip';
  if (meta.lqip) lqip.style.backgroundImage = `url("${meta.lqip}")`;
  root.appendChild(lqip);

  const img = document.createElement('img');
  img.className = 'pv-img';
  img.alt = '';
  img.draggable = false;
  img.decoding = 'async';
  img.addEventListener('load', () => img.classList.add('pv-img-loaded'));
  root.appendChild(img);

  // Page number tag (interior pages only) — the reference clients use in notes.
  if (meta.kind === 'page') {
    const pn = document.createElement('span');
    pn.className = 'pv-pn';
    pn.textContent = String(meta.n);
    root.appendChild(pn);
  }

  // Gold note marker — toggled imperatively when notes change.
  const dot = document.createElement('span');
  dot.className = 'pv-dot';
  root.appendChild(dot);

  return { meta, root, img, dot };
}

/** Attach sharp JPEGs near `cur`, release decoded bitmaps far away. */
function syncImages(els: PageEl[], cur: number) {
  for (let i = 0; i < els.length; i++) {
    const { img, meta } = els[i];
    const d = Math.abs(i - cur);
    if (d <= SHARP_WINDOW) {
      if (meta.url && img.dataset.state !== 'sharp') {
        img.dataset.state = 'sharp';
        img.src = meta.url;
      }
    } else if (d > KEEP_WINDOW && img.dataset.state === 'sharp') {
      img.dataset.state = '';
      img.classList.remove('pv-img-loaded');
      img.removeAttribute('src');
    }
  }
}

function syncDots(els: PageEl[], notedPages: Set<number>) {
  for (const el of els) {
    const notePage = notePageFor(el.meta.kind, el.meta.n);
    el.dot.style.display = notePage != null && notedPages.has(notePage) ? 'block' : 'none';
  }
}

// ─── Status copy (NO paper sizes anywhere) ───────────────────────────────────
const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  approved: { label: 'Album confirmé ✓', cls: 'pv-badge--ok' },
  changes_requested: { label: 'Modifications demandées', cls: 'pv-badge--warn' },
};

// ─── Component ───────────────────────────────────────────────────────────────

// ─── COACH MARKS: A FEW SECONDS, ONCE PER PHONE (owner, 2026-10-21) ─────────
// « It keeps blinking, it makes the client uncomfortable. » Each hint shows for
// COACH_VISIBLE_MS, pulses twice (index.css), and is remembered on this device
// so a client who comes back to her preview is never shown it again.
const COACH_VISIBLE_MS = 3500;
const COACH_KEY = (k: 'swipe' | 'note') => `sz-pv-coach-${k}`;
function coachSeen(k: 'swipe' | 'note'): boolean {
  try { return localStorage.getItem(COACH_KEY(k)) === '1'; } catch { return false; }
}
function markCoachSeen(k: 'swipe' | 'note'): void {
  try { localStorage.setItem(COACH_KEY(k), '1'); } catch { /* private mode: once per visit */ }
}

export default function PreviewBookPage() {
  const { token } = useParams<{ token: string }>();
  // `?album=N` (parcel scanner, 2026-10-01): the album NUMBER to open on,
  // 1-based. Applied once, the first time a rendered book is on the shelf;
  // absent or naming no rendered album → the first album, as before.
  const { search } = useLocation();
  const requestedAlbumRef = useRef(parseAlbumParam(search));
  // `?page=N` (2026-10-07): straight into the book, open at that page — once.
  const requestedPageRef = useRef(parsePageParam(search));
  const albumChosenRef = useRef(false);

  const [data, setData] = useState<PreviewPublicData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [albumIdx, setAlbumIdx] = useState(0); // index into data.albums
  const [mode, setMode] = useState<'landing' | 'reading'>(() => (requestedPageRef.current ? 'reading' : 'landing'));
  const [pageIdx, setPageIdx] = useState(0);
  const [pageTotal, setPageTotal] = useState(0);
  const [notes, setNotes] = useState<PreviewNote[]>([]);
  const [reviewStatus, setReviewStatus] = useState<string | null>(null);

  // Note sheet
  const [noteSheetPage, setNoteSheetPage] = useState<number | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);

  // Confirm flow
  const [confirmSheet, setConfirmSheet] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  // ⚠️ NOT A TOAST. A failed confirmation used to flash for 3.2s at the bottom
  // of a phone and vanish, so a customer who pressed « Oui, je confirme » and
  // got a 404 believed they had approved their album. This sits INSIDE the
  // sheet, directly above the button they are looking at, and stays.
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [resultModal, setResultModal] =
    useState<'approved' | 'changes_requested' | null>(null);

  const [toast, setToast] = useState<string | null>(null);

  // Coach marks — gentle animated onboarding: "swipe to flip" on first open,
  // "tap a page to leave a note" right after the first flip. Each shows once
  // per visit and dissolves on the action it teaches (or after a few seconds).
  const [coach, setCoach] = useState<'swipe' | 'note' | null>(null);
  const coachShownRef = useRef({ swipe: false, note: false });
  const coachTimersRef = useRef<number[]>([]);

  const hostRef = useRef<HTMLDivElement>(null);
  const flipRef = useRef<PageFlip | null>(null);
  const pageElsRef = useRef<PageEl[]>([]);

  const album = data?.albums[albumIdx] ?? null;
  // Count the unfinished ones too: a Duo where only album 2 rendered still has
  // TWO albums to talk about, and hiding the chip row would hide the
  // "en préparation" status that explains the missing book (#1144).
  const multiAlbum =
    ((data?.albums.length ?? 0) + (data?.pending_albums?.length ?? 0)) > 1;

  const openNotePages = useMemo(() => {
    const set = new Set<number>();
    if (album) {
      for (const n of notes) {
        if (!n.resolved_at && n.album_index === album.album_index) set.add(n.page_number);
      }
    }
    return set;
  }, [notes, album]);

  const openNoteCount = useMemo(
    () => notes.filter((n) => !n.resolved_at).length,
    [notes],
  );

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  // ── Load + adaptive poll (2026-07-23) ──────────────────────────────────────
  // The book can be MID-RENDER when opened (or a "Régénérer" can produce a NEW
  // version while the client is watching). A one-shot fetch hung forever on the
  // spinner and never showed a regenerated book. Now:
  //   • fast poll (4s) while previews are 'generating' / no manifest yet, until
  //     ready / failed / a ~3-min cap → deterministic, never an infinite spinner;
  //   • slow watch (12s; 5s while the « version précédente » banner shows)
  //     once ready, tracking EVERY album's own manifest date → when a NEW
  //     render lands (regen), swap the book automatically (#1248);
  //   • only setData when the render actually CHANGED so the flipbook isn't
  //     torn down on every poll — and a swap keeps the reader's spread;
  //   • paused while the tab is hidden.
  const [stillPreparing, setStillPreparing] = useState(false);
  // Read on EVERY poll, not only when a new render lands: an edit made while
  // this page is open makes the shown book old without changing it (#1241).
  // The albums behind their edits (null = a worker too old to say which → every album).
  const [outdatedAlbums, setOutdatedAlbums] = useState<number[] | null>([]);
  // The quick redraw of a new version, per album, while it runs (2026-10-21):
  // a live « en préparation » bar. The book on screen stays the complete old
  // one until the complete new one replaces it.
  const [buildingQuick, setBuildingQuick] = useState<NonNullable<PreviewPublicData['building']>['quick']>([]);
  // The « version précédente » banner, on the album that is behind only (#1243).
  const outdated = outdatedAlbums === null || (album != null && outdatedAlbums.includes(album.album_index));
  const buildingPct = buildingPctFor(buildingQuick, album?.album_index ?? null);
  // The book on screen (#1248): its albums and their own dates — see lib/previewBookVersion.
  const shownBookRef = useRef<PreviewPublicData | null>(null);
  // A swap rebuilds the flipbook; this carries the reader's spread across it.
  const resumeFlipIdxRef = useRef<number | null>(null);
  useEffect(() => {
    if (!token) { setLoadError('Lien introuvable'); return; }
    const tk: string = token;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const FAST_MAX = 45; // 45 × 4s ≈ 3 min of "generating"
    let fastTries = 0;
    let firstLoad = true;

    const schedule = (ms: number) => {
      timer = null; // cleared so the visibility handler knows nothing is pending
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return; // resumes on visibility
      timer = setTimeout(tick, ms);
    };

    function tick() {
      previewLinkApi.public(tk)
        .then((d) => {
          if (cancelled) return;
          // SHOW WHAT RENDERED. An order-level 'failed' must never hide albums
          // that DID render: on a Duo where the customer built only album 2,
          // album 1 is an empty slot, not a broken album, and #1144 showed the
          // customer "contactez l'équipe" while album 2 sat perfectly
          // rasterized on storage. Only a total absence of albums is a dead end.
          const hasBook = d.albums.length > 0;
          const behind = Array.isArray(d.preview_outdated_albums) ? d.preview_outdated_albums : d.preview_outdated ? null : [];
          setOutdatedAlbums(behind);
          setBuildingQuick(d.building?.quick ?? []);
          const ready = d.previews_status === 'ready' && hasBook;

          // Update the rendered book ONLY on first load or a genuinely new
          // render — avoids rebuilding the flipbook (and losing the page) on
          // every unchanged poll. ⚠️ « New » = ANY album's own manifest date
          // moved (#1248): the order-level stamp is not written by every path
          // that draws pages, and keying on it left the old book on screen.
          if (shouldSwapBook({ firstLoad, shown: shownBookRef.current, next: d })) {
            const isSwap = !firstLoad;
            const prevShown = shownBookRef.current;
            firstLoad = false;
            shownBookRef.current = d;
            if (isSwap) {
              resumeFlipIdxRef.current = flipRef.current?.getCurrentPageIndex() ?? null;
              // Same album on screen even when the shelf grew (album 1 finished while she read album 2).
              if (prevShown) setAlbumIdx((i) => albumIdxAfterSwap(prevShown, d, i));
            }
            setData(d);
            setNotes(d.notes);
            setReviewStatus(d.order.client_review_status);
            // Once only: a later render (a regen) keeps the album the
            // customer is looking at.
            if (!albumChosenRef.current && d.albums.length > 0) {
              albumChosenRef.current = true;
              setAlbumIdx(initialAlbumIdx(d.albums, requestedAlbumRef.current));
            }
            if (isSwap) showToast('Nouvelle version de l’album chargée ✨');
          }

          if (d.previews_status === 'failed' && !hasBook) {
            setLoadError('La prévisualisation n’a pas pu être générée. Contactez l’équipe Storiz.');
            return;
          }
          // While the page tells the customer a new version is on its way, look
          // more often — the quick preview lands it within seconds of « Régénérer ».
          const watchMs = (d.building?.quick ?? []).length > 0 ? POLL_BUILDING_MS
            : behind === null || behind.length > 0 ? POLL_OUTDATED_MS : POLL_WATCH_MS;
          if (ready) { setStillPreparing(false); schedule(watchMs); return; } // keep watching for a regen
          // Partially rendered: at least one album is viewable. Show it and keep
          // watching rather than parking the customer on a spinner.
          if (hasBook) { setStillPreparing(false); schedule(watchMs); return; }
          // Still cooking (generating / manifest not written yet).
          if (fastTries < FAST_MAX) { fastTries += 1; schedule(4000); }
          else { setStillPreparing(true); schedule(12000); } // gave up fast; keep a slow watch
        })
        // ⚠️ A DEAD LINK MUST NEVER BE SWALLOWED. This used to reschedule on
        // ANY error once the first load had succeeded, so a link that died
        // under the customer left the page showing the old book with a
        // live-looking « Confirmer mon album » button. On #WA-823663 the
        // customer pressed it, the POST 404'd behind a 3-second toast, and the
        // order sat at `pending` while everyone waited on an approval that had
        // already been given. 404/410 are permanent and are shown; everything
        // else is a hiccup worth retrying.
        .catch((e: Error & { status?: number }) => {
          if (cancelled) return;
          if (e.status === 404 || e.status === 410) { setLoadError(e.message); return; }
          if (firstLoad) setLoadError(e.message); else schedule(12000);
        });
    }

    tick();
    const onVis = () => { if (document.visibilityState === 'visible' && !cancelled && !timer) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; if (timer) clearTimeout(timer); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── PageFlip lifecycle (imperative — see header comment) ──────────────────
  useEffect(() => {
    if (mode !== 'reading' || !album || !hostRef.current) return;
    const host = hostRef.current;

    const metas = buildMetas(album);
    const els = metas.map(buildPageElement);
    pageElsRef.current = els;
    for (const el of els) host.appendChild(el.root);

    const aspect = album.page_aspect > 0 ? album.page_aspect : 0.713;
    const maxH = Math.max(320, Math.min(940, window.innerHeight - 215));
    // Real-book spread: two pages side by side, always. Per-page width is half
    // the host (the open book), capped so the spread never exceeds maxH tall.
    const hostW = host.clientWidth || Math.min(window.innerWidth * 0.94, 920);
    const pageW = Math.max(140, Math.min(Math.floor(hostW / 2), Math.round(maxH * aspect)));
    const pageH = Math.round(pageW / aspect);

    const pf = new PageFlip(host, {
      width: pageW,
      height: pageH,
      size: 'stretch',
      minWidth: 120,
      maxWidth: pageW,
      minHeight: 160,
      maxHeight: pageH,
      showCover: true,
      usePortrait: false, // force the two-page landscape spread on every screen
      mobileScrollSupport: false,
      drawShadow: true,
      flippingTime: 700,
      maxShadowOpacity: 0.35,
      showPageCorners: true,
      disableFlipByClick: true, // taps are for notes; flipping = swipe / corner-drag / arrows
      swipeDistance: 24,
    });
    pf.loadFromHTML(els.map((e) => e.root));
    flipRef.current = pf;
    setPageIdx(0);
    setPageTotal(els.length);

    pf.on('flip', (e) => {
      const idx = typeof e.data === 'number' ? e.data : parseInt(String(e.data), 10) || 0;
      setPageIdx(idx);
      syncImages(els, idx);
      // The flip itself proves "swipe" is learned — dissolve that mark, then
      // teach the note tap once the page settles (only over interior pages).
      setCoach((c) => (c === 'swipe' ? null : c));
      if (!coachShownRef.current.note && !coachSeen('note') && idx >= 1 && idx < els.length - 1) {
        coachShownRef.current.note = true;
        markCoachSeen('note');
        coachTimersRef.current.push(window.setTimeout(() => setCoach('note'), 950));
        coachTimersRef.current.push(window.setTimeout(
          () => setCoach((c) => (c === 'note' ? null : c)), 950 + COACH_VISIBLE_MS,
        ));
      }
    });

    syncImages(els, 0);
    // A swap (a new version landed) keeps the spread the reader was on.
    if (resumeFlipIdxRef.current != null && resumeFlipIdxRef.current > 0) {
      const at = Math.min(resumeFlipIdxRef.current, els.length - 1);
      resumeFlipIdxRef.current = null;
      try { pf.turnToPage(at); } catch { /* fresh book on page 1 is fine */ }
      const now = pf.getCurrentPageIndex();
      setPageIdx(now);
      syncImages(els, now);
    }
    resumeFlipIdxRef.current = null;
    if (requestedPageRef.current) {
      pf.turnToPage(flipIndexForPage(requestedPageRef.current, els.length));
      requestedPageRef.current = null;
      const at = pf.getCurrentPageIndex();
      setPageIdx(at);
      syncImages(els, at);
    }

    // First entry into the reader → invite the swipe (once per visit).
    if (!coachShownRef.current.swipe && !coachSeen('swipe')) {
      coachShownRef.current.swipe = true;
      markCoachSeen('swipe');
      coachTimersRef.current.push(window.setTimeout(() => setCoach('swipe'), 800));
      coachTimersRef.current.push(window.setTimeout(
        () => setCoach((c) => (c === 'swipe' ? null : c)), 800 + COACH_VISIBLE_MS,
      ));
    }

    // Tap detection — pointer events still fire when PageFlip preventDefaults
    // touch events, unlike synthetic clicks. Small move/time guard so the end
    // of a swipe never counts as a tap.
    let downAt: { x: number; y: number; t: number } | null = null;
    const onDown = (e: PointerEvent) => { downAt = { x: e.clientX, y: e.clientY, t: Date.now() }; };
    const onUp = (e: PointerEvent) => {
      if (!downAt) return;
      const moved = Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y);
      const dt = Date.now() - downAt.t;
      downAt = null;
      if (moved > 12 || dt > 600) return;
      const target = (e.target as HTMLElement).closest('.pv-page');
      if (!target) return;
      const el = els.find((p) => p.root === target);
      if (!el) return;
      // Every sheet that prints takes a note — the covers too (#1210: a tap
      // on the front cover used to flip the book open, so the cover could
      // never be annotated). Flipping = swipe / corner-drag / the ‹ › arrows.
      const notePage = notePageFor(el.meta.kind, el.meta.n);
      if (notePage != null) openNoteSheet(notePage);
    };
    host.addEventListener('pointerdown', onDown);
    host.addEventListener('pointerup', onUp);

    return () => {
      host.removeEventListener('pointerdown', onDown);
      host.removeEventListener('pointerup', onUp);
      coachTimersRef.current.forEach((t) => window.clearTimeout(t));
      coachTimersRef.current = [];
      setCoach(null);
      try { pf.destroy(); } catch { /* PageFlip throws if already torn down */ }
      flipRef.current = null;
      pageElsRef.current = [];
      host.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, albumIdx, data]);

  // Note dots follow note state.
  useEffect(() => {
    syncDots(pageElsRef.current, openNotePages);
  }, [openNotePages]);

  // ── Notes ─────────────────────────────────────────────────────────────────
  const openNoteSheet = (page: number) => {
    setCoach(null); // the tap just happened — the coach mark has done its job
    setNoteDraft('');
    setNoteSheetPage(page);
  };

  const pageOpenNotes = useMemo(() => {
    if (noteSheetPage == null || !album) return [];
    return notes.filter(
      (n) => !n.resolved_at && n.album_index === album.album_index && n.page_number === noteSheetPage,
    );
  }, [notes, noteSheetPage, album]);

  const handleAddNote = async () => {
    if (!token || !album || noteSheetPage == null) return;
    const text = noteDraft.trim();
    if (!text) return;
    setNoteBusy(true);
    try {
      const { note } = await previewLinkApi.addNote(token, {
        album_index: album.album_index,
        page_number: noteSheetPage,
        note: text,
      });
      setNotes((prev) => [...prev, note]);
      setNoteDraft('');
      setNoteSheetPage(null);
      showToast(`Note ajoutée — ${notePlaceLabel(noteSheetPage).toLowerCase()} ✓`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Impossible d’enregistrer la note');
    } finally {
      setNoteBusy(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!token) return;
    setNoteBusy(true);
    try {
      await previewLinkApi.deleteNote(token, noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Suppression impossible');
    } finally {
      setNoteBusy(false);
    }
  };

  // ── Confirm ───────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!token) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      const res = await previewLinkApi.confirm(token);
      setReviewStatus(res.client_review_status);
      setConfirmSheet(false);
      setResultModal(res.client_review_status);
    } catch (e) {
      const err = e as Error & { status?: number };
      // A dead link is not a retryable hiccup — the sheet's "try again" button
      // would never work. Say so on the whole page instead.
      if (err.status === 404 || err.status === 410) {
        setConfirmSheet(false);
        setLoadError(err.message);
        return;
      }
      // 409 = the album is mid-rebuild (see backToRender.ts). The message the
      // worker sends already tells them to reload, so it is shown verbatim.
      setConfirmError(err.message || 'Une erreur est survenue. Réessayez.');
    } finally {
      setConfirmBusy(false);
    }
  };

  // ── Render: error / loading ───────────────────────────────────────────────
  if (loadError) {
    return (
      <div className="pv-root pv-grain pv-center">
        <div className="pv-fade-up" style={{ textAlign: 'center', padding: 24, maxWidth: 420 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📖</div>
          <h1 className="pv-serif" style={{ fontSize: 26, margin: '0 0 10px' }}>{loadError}</h1>
          <p style={{ color: 'rgba(250,247,242,.65)', fontSize: 15, lineHeight: 1.6 }}>
            Ce lien d&rsquo;aperçu n&rsquo;est pas (ou plus) disponible.
            Contactez l&rsquo;équipe Storiz pour en recevoir un nouveau.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="pv-root pv-grain pv-center">
        <div style={{ textAlign: 'center' }}>
          <div className="pv-spinner" />
          <p style={{ color: 'rgba(250,247,242,.6)', fontSize: 14, marginTop: 18 }}>
            Préparation de votre album…
          </p>
        </div>
      </div>
    );
  }

  if (data.albums.length === 0) {
    // Still being built. While polling, show a live "preparing" state that
    // updates itself (no manual reload); after the ~3-min cap, a soft "come
    // back shortly" — never an infinite spinner.
    return (
      <div className="pv-root pv-grain pv-center">
        <div className="pv-fade-up" style={{ textAlign: 'center', padding: 24, maxWidth: 420 }}>
          {stillPreparing ? (
            <>
              <div style={{ fontSize: 40, marginBottom: 16 }}>🕰️</div>
              <h1 className="pv-serif" style={{ fontSize: 26, margin: '0 0 10px' }}>Encore un instant…</h1>
              <p style={{ color: 'rgba(250,247,242,.65)', fontSize: 15, lineHeight: 1.6 }}>
                La préparation de votre album prend un peu plus de temps que prévu.
                Laissez cette page ouverte ou revenez dans quelques minutes — elle se
                mettra à jour toute seule.
              </p>
            </>
          ) : (
            <>
              <div className="pv-spinner" style={{ margin: '0 auto 18px' }} />
              <h1 className="pv-serif" style={{ fontSize: 24, margin: '0 0 10px' }}>Préparation de votre album…</h1>
              <p style={{ color: 'rgba(250,247,242,.65)', fontSize: 14.5, lineHeight: 1.6 }}>
                Votre livre 3D se construit. Cette page se mettra à jour automatiquement
                dès qu&rsquo;il est prêt.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  const firstName = data.order.customer_first_name;
  const badge = reviewStatus ? STATUS_BADGE[reviewStatus] : null;

  // ── Render: landing (closed 3D book) ─────────────────────────────────────
  if (mode === 'landing') {
    const front = album?.cover?.front;
    const spine = album?.cover?.spine;
    const frontAspect = front?.aspect || 0.713;
    // Spine thickness in px relative to the rendered cover height.
    const coverH = 'min(52dvh, 420px)';
    return (
      <div className="pv-root pv-grain pv-landing">
        <header className="pv-landing-head pv-fade-up">
          <span className="pv-wordmark pv-serif">Storiz</span>
          {badge && <span className={`pv-badge ${badge.cls}`}>{badge.label}</span>}
        </header>
        {outdated && (
          <p className="pv-outdated" role="status">
            Vous avez modifié votre album — cet aperçu montre la version précédente.
            {buildingPct != null ? ' La nouvelle version est en préparation…' : ' La nouvelle version arrive ici toute seule dans quelques minutes.'}
            {buildingPct != null && <span className="pv-building" aria-hidden><span style={{ width: `${buildingPct}%` }} /></span>}
          </p>
        )}

        <main className="pv-landing-main">
          <div className="pv-fade-up" style={{ animationDelay: '.08s', textAlign: 'center' }}>
            <p className="pv-eyebrow">{firstName ? `${firstName}, votre album est prêt` : 'Votre album est prêt'}</p>
            <h1 className="pv-serif pv-landing-title">
              Vos souvenirs,<br />reliés à la main.
            </h1>
          </div>

          <button
            type="button"
            className="pv-book3d pv-fade-up"
            style={{ animationDelay: '.18s' }}
            onClick={() => setMode('reading')}
            aria-label="Ouvrir l'album"
          >
            <div className="pv-book3d-inner" style={{ height: coverH, aspectRatio: String(frontAspect) }}>
              <div className="pv-cover-spine" style={spine?.url ? { backgroundImage: `url("${spine.url}")` } : undefined} />
              <div className="pv-cover-edge" />
              {front?.lqip && (
                <div className="pv-cover-lqip" style={{ backgroundImage: `url("${front.lqip}")` }} />
              )}
              {front?.url && <img className="pv-cover-front" src={front.url} alt="Couverture de votre album" draggable={false} />}
              <div className="pv-cover-sheen" />
            </div>
          </button>

          <div className="pv-fade-up" style={{ animationDelay: '.3s', textAlign: 'center' }}>
            <span className="pv-hint">Feuilletez votre album&nbsp;👆</span>
            {multiAlbum && (
              <div className="pv-chips" style={{ marginTop: 18 }}>
                {data.albums.map((a, i) => (
                  <button
                    key={a.album_index}
                    type="button"
                    className={`pv-chip${i === albumIdx ? ' pv-chip--on' : ''}`}
                    onClick={() => setAlbumIdx(i)}
                  >
                    Album {a.album_index}
                  </button>
                ))}
                {/* Bought but not finished. Named rather than absent, so a Duo
                    customer seeing one book knows the other is still coming
                    instead of assuming something is broken (#1144). */}
                {(data.pending_albums ?? []).map((n) => (
                  <span key={`pending-${n}`} className="pv-chip pv-chip--pending" aria-disabled="true">
                    Album {n} · {data.preview_stopped ? 'indisponible' : 'en préparation'}
                  </span>
                ))}
              </div>
            )}
            {/* An abandoned album must never keep saying "en préparation"
                (#1144). The chip above changes wording; this says what to do
                about it, in the customer's terms — no error codes, no blame. */}
            {data.preview_stopped && (data.pending_albums?.length ?? 0) > 0 && (
              <p className="pv-pending-note">
                L’aperçu de {(data.pending_albums ?? []).length > 1 ? 'ces albums' : 'cet album'} n’a
                pas pu être préparé. Votre livre est bien commandé — écrivez-nous et on s’en occupe.
              </p>
            )}
          </div>
        </main>

        <footer className="pv-landing-foot pv-fade-up" style={{ animationDelay: '.4s' }}>
          Touchez le livre pour le découvrir, page après page.
        </footer>
      </div>
    );
  }

  // ── Render: reading mode ──────────────────────────────────────────────────
  const interiorCount = album?.page_count ?? 0;
  // Landscape spread: pageIdx is the LEFT page of the open spread
  // (meta index == interior page number; 0 = cover, pageTotal-1 = back cover).
  const pageLabel = (() => {
    // Meta layout: 0 = cover · 1 = blank inside-cover · 2..N+1 = pages 1..N ·
    // N+2 = blank inside-back · N+3 = back cover. Left page number = idx - 1.
    if (pageIdx === 0) return 'Couverture';
    if (pageIdx >= pageTotal - 1) return 'Dos de l’album';
    const leftN = pageIdx - 1;            // 0 when the left side is the blank endpaper
    const rightN = leftN + 1;
    // This count is the PRINTED book: it includes the Storiz intro and outro
    // pages, so it runs 2 higher than the editor's "your pages" total and each
    // number sits one ahead. Both are correct; saying which one this is stops
    // staff and clients quoting different numbers for the same spread.
    if (leftN < 1) return `Page ${rightN} sur ${interiorCount}`;   // blank | page 1
    if (rightN > interiorCount) return `Page ${leftN} sur ${interiorCount}`; // page N | blank
    return `Pages ${leftN}–${rightN} sur ${interiorCount}`;
  })();
  const pageLabelHint = pageIdx > 0 && pageIdx < pageTotal - 1
    ? 'Numérotation du livre imprimé (pages Storiz incluses)'
    : null;

  return (
    <div className="pv-root pv-grain pv-reader">
      {outdated && (
        <p className="pv-outdated" role="status">
          Vous avez modifié votre album — cet aperçu montre la version précédente.
          {buildingPct != null ? ' La nouvelle version est en préparation…' : ' La nouvelle version arrive ici toute seule dans quelques minutes.'}
          {buildingPct != null && <span className="pv-building" aria-hidden><span style={{ width: `${buildingPct}%` }} /></span>}
        </p>
      )}
      {/* Top bar */}
      <header className="pv-bar">
        <button type="button" className="pv-bar-back" onClick={() => setMode('landing')} aria-label="Retour">
          ‹
        </button>
        <div className="pv-bar-mid">
          {multiAlbum ? (
            <div className="pv-chips">
              {data.albums.map((a, i) => (
                <button
                  key={a.album_index}
                  type="button"
                  className={`pv-chip${i === albumIdx ? ' pv-chip--on' : ''}`}
                  onClick={() => { setAlbumIdx(i); }}
                >
                  Album {a.album_index}
                </button>
              ))}
              {(data.pending_albums ?? []).map((n) => (
                <span key={`pending-${n}`} className="pv-chip pv-chip--pending" aria-disabled="true">
                  Album {n} · en préparation
                </span>
              ))}
            </div>
          ) : (
            <span className="pv-wordmark pv-serif" style={{ fontSize: 17 }}>Storiz</span>
          )}
        </div>
        {reviewStatus === 'approved' ? (
          <span className="pv-badge pv-badge--ok">Confirmé ✓</span>
        ) : (
          <button type="button" className="pv-confirm-pill" onClick={() => setConfirmSheet(true)}>
            Confirmer
          </button>
        )}
      </header>

      {/* The book */}
      <div className="pv-stage">
        {/* key includes the rendered version (every album's own date, #1248) so
            an auto-swap (regen) remounts a FRESH host node — PageFlip re-init on
            an already-used host renders blank (DOM-reparent gotcha), but a fresh
            mount is the known-good path. Any data change MUST change this key. */}
        <div
          ref={hostRef}
          className="pv-book-host"
          key={`album-${albumIdx}-${bookVersionKey(data)}`}
        />

        {/* Coach marks — pointer-events:none so gestures reach the book. */}
        {coach === 'swipe' && (
          <div className="pv-coach" aria-hidden="true">
            <div className="pv-coach-swipe">
              <span className="pv-coach-chevron">‹</span>
              <span className="pv-coach-dot" />
            </div>
            <span className="pv-coach-caption">Glissez pour tourner les pages</span>
          </div>
        )}
        {coach === 'note' && (
          <div className="pv-coach" aria-hidden="true">
            <div className="pv-coach-tap">
              <span className="pv-coach-ring" />
              <span className="pv-coach-ring pv-coach-ring--late" />
              <span className="pv-coach-tapcore" />
            </div>
            <span className="pv-coach-caption">Touchez une page pour laisser une note</span>
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <footer className="pv-controls">
        <button
          type="button" className="pv-nav" aria-label="Page précédente"
          onClick={() => flipRef.current?.flipPrev()} disabled={pageIdx <= 0}
        >‹</button>
        <div className="pv-pageinfo">
          <span title={pageLabelHint ?? undefined}>{pageLabel}</span>
          <span className="pv-pageinfo-sub">
            {openNotePages.size > 0
              ? `${openNotePages.size} page${openNotePages.size > 1 ? 's' : ''} annotée${openNotePages.size > 1 ? 's' : ''}`
              : 'Touchez une page pour laisser une note'}
          </span>
        </div>
        <button
          type="button" className="pv-nav" aria-label="Page suivante"
          onClick={() => flipRef.current?.flipNext()} disabled={pageIdx >= pageTotal - 1}
        >›</button>
      </footer>

      {reviewStatus !== 'approved' && (
        <div className="pv-cta-wrap">
          <button type="button" className="pv-cta" onClick={() => setConfirmSheet(true)}>
            {openNoteCount > 0
              ? `Envoyer mes ${openNoteCount} remarque${openNoteCount > 1 ? 's' : ''}`
              : 'Confirmer mon album'}
          </button>
        </div>
      )}

      {/* ── Note bottom sheet ── */}
      {noteSheetPage != null && (
        <div className="pv-veil" onClick={() => !noteBusy && setNoteSheetPage(null)}>
          <div className="pv-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="pv-sheet-grab" />
            <h2 className="pv-serif" style={{ fontSize: 20, margin: '0 0 4px' }}>
              {notePlaceLabel(noteSheetPage)}
            </h2>
            <p style={{ color: 'rgba(250,247,242,.55)', fontSize: 13, margin: '0 0 14px' }}>
              {isCoverNotePage(noteSheetPage)
                ? <>Une retouche à demander sur la couverture&nbsp;? Écrivez-la ici —
                  notre équipe s&rsquo;en occupe.</>
                : <>Une retouche à demander sur cette page&nbsp;? Écrivez-la ici —
                  notre équipe s&rsquo;en occupe.</>}
            </p>

            {pageOpenNotes.map((n) => (
              <div key={n.id} className="pv-note-item">
                <span style={{ flex: 1 }}>{n.note}</span>
                <button
                  type="button" className="pv-note-del" disabled={noteBusy}
                  onClick={() => handleDeleteNote(n.id)} aria-label="Supprimer la note"
                >✕</button>
              </div>
            ))}

            <textarea
              className="pv-textarea"
              placeholder="Ex. : remplacer cette photo, corriger le texte…"
              value={noteDraft}
              maxLength={MAX_NOTE_CHARS}
              rows={3}
              onChange={(e) => setNoteDraft(e.target.value)}
            />
            <div className="pv-sheet-actions">
              <button type="button" className="pv-btn-ghost" onClick={() => setNoteSheetPage(null)} disabled={noteBusy}>
                Fermer
              </button>
              <button
                type="button" className="pv-btn-gold"
                onClick={handleAddNote} disabled={noteBusy || !noteDraft.trim()}
              >
                {noteBusy ? 'Enregistrement…' : 'Ajouter la note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm sheet ── */}
      {confirmSheet && (
        <div className="pv-veil" onClick={() => { if (!confirmBusy) { setConfirmSheet(false); setConfirmError(null); } }}>
          <div className="pv-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="pv-sheet-grab" />
            {openNoteCount > 0 ? (
              <>
                <h2 className="pv-serif" style={{ fontSize: 21, margin: '0 0 8px' }}>
                  Envoyer vos remarques&nbsp;?
                </h2>
                <p style={{ color: 'rgba(250,247,242,.65)', fontSize: 14, lineHeight: 1.6, margin: '0 0 18px' }}>
                  Vous avez laissé {openNoteCount} note{openNoteCount > 1 ? 's' : ''}.
                  Notre équipe applique vos modifications, puis vous recevrez
                  un nouvel aperçu à confirmer.
                </p>
              </>
            ) : (
              <>
                <h2 className="pv-serif" style={{ fontSize: 21, margin: '0 0 8px' }}>
                  Tout est parfait&nbsp;?
                </h2>
                <p style={{ color: 'rgba(250,247,242,.65)', fontSize: 14, lineHeight: 1.6, margin: '0 0 18px' }}>
                  En confirmant, vous validez votre album tel que vous venez
                  de le feuilleter — il part ensuite à l&rsquo;impression.
                </p>
              </>
            )}
            {confirmError && (
              <p className="pv-confirm-error" role="alert">{confirmError}</p>
            )}
            <div className="pv-sheet-actions">
              <button
                type="button" className="pv-btn-ghost" disabled={confirmBusy}
                onClick={() => { setConfirmSheet(false); setConfirmError(null); }}
              >
                Pas encore
              </button>
              <button type="button" className="pv-btn-gold" onClick={handleConfirm} disabled={confirmBusy}>
                {confirmBusy
                  ? 'Envoi…'
                  : confirmError ? 'Réessayer'
                  : openNoteCount > 0 ? 'Envoyer mes remarques' : 'Oui, je confirme'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Result modal ── */}
      {resultModal && (
        <div className="pv-veil pv-veil--center" onClick={() => setResultModal(null)}>
          <div className="pv-modal" onClick={(e) => e.stopPropagation()}>
            {resultModal === 'approved' ? (
              <>
                <div style={{ fontSize: 44, marginBottom: 12 }}>🎉</div>
                <h2 className="pv-serif" style={{ fontSize: 23, margin: '0 0 8px' }}>
                  Merci{firstName ? ` ${firstName}` : ''}&nbsp;!
                </h2>
                <p style={{ color: 'rgba(250,247,242,.7)', fontSize: 14.5, lineHeight: 1.65, margin: '0 0 18px' }}>
                  Votre album est confirmé. Nous lançons l&rsquo;impression
                  et le préparons avec le plus grand soin. ✨
                </p>
              </>
            ) : (
              <>
                <div style={{ fontSize: 44, marginBottom: 12 }}>📝</div>
                <h2 className="pv-serif" style={{ fontSize: 23, margin: '0 0 8px' }}>
                  Vos remarques sont bien reçues
                </h2>
                <p style={{ color: 'rgba(250,247,242,.7)', fontSize: 14.5, lineHeight: 1.65, margin: '0 0 18px' }}>
                  Notre équipe applique vos modifications et revient vers vous
                  très vite avec un nouvel aperçu.
                </p>
              </>
            )}
            <button type="button" className="pv-btn-gold" style={{ width: '100%' }} onClick={() => setResultModal(null)}>
              Continuer à feuilleter
            </button>
          </div>
        </div>
      )}

      {toast && <div className="pv-toast">{toast}</div>}
    </div>
  );
}
